import React from "react";
import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { LuSendHorizonal } from "react-icons/lu";
import { SeedType, get_user_data, giveErrorMessage } from "../../utils/common";
import InfiniteScroll from "react-infinite-scroll-component";
import { SeedItem } from "../../components/SeedItem/SeedItem";
import MultipleLoadingScreen from "../../design-system/MultipleLoadingScreen";
import useRefreshToken from "../../utils/useRefreshToken";
import { getPodcastCitationInfo, getSeedFileInfo, getSeedFiles, getSeedList, searchSeedAPI } from "../../api/axiosApi";
import { textarea } from "@material-tailwind/react";
import CitationExplorer from "./CitationExplorer";
import ShowCitationExplorerSource from "./ShowCitationExplorerSource";

const Seed = () => {
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const [seeds, setSeeds] = useState<SeedType[]>([]);
    const [error, setError] = useState<string | null>("");
    const [textAreaValue, setTextAreaValue] = useState<string>("");
    const [hasSearched, setHasSearched] = useState(false);
    const [searchLoading, SetSearchLoading] = useState<boolean>(false);
    const [showCitationSource, setShowCitationSource] = useState(false);
    const [openRight, setOpenRight] = useState(false);
    const [sourcesCount, setSourceCount] = useState({
        blogs: 0,
        exclusive_content: 0,
        sps_podcast: 0,
        // fullstack_leadership_podcast: 0,
        books: 0
    });
    const [citation, setCitation] = useState();

    const openDrawerRight = () => {
        setOpenRight(true);
    };
    const closeDrawerRight = () => {
        setOpenRight(false);
        setShowCitationSource(false);
    };

    function onCitationClicked(citation: any) {
        setCitation(citation);
        setShowCitationSource(true);
        openDrawerRight();
    }

    const searchSeeds = useCallback(async () => {
        SetSearchLoading(true);
        setError(null);
        setHasSearched(true);

        if (textAreaValue.trim().length !== 0) {
            try {
                const res = await searchSeedAPI(textAreaValue);
                const result = await res.data;
                const uniqueCitations = new Set();
                const newFilteredSeeds: SeedType[] = [];
                const count = {
                    blogs: 0,
                    exclusive_content: 0,
                    sps_podcast: 0,
                    // fullstack_leadership_podcast: 0,
                    books: 0
                };

                for (let citationObj of result.fileName) {
                    const filename = citationObj.citation
                        .split("/")
                        .pop()
                        ?.substring(0, citationObj.citation?.split("/").pop()?.lastIndexOf("."));
                    if (citationObj.media_type === "blog" || citationObj.media_type === "exclusive_content") {
                        try {
                            if (filename && !uniqueCitations.has(filename)) {
                                uniqueCitations.add(filename);
                                const Response = await getSeedFileInfo(filename);
                                const seedInfo = await Response.data;
                                if (citationObj.media_type === "blog") count.blogs += 1;
                                else if (citationObj.media_type === "exclusive_content") count.exclusive_content += 1;
                                seedInfo.fileName = filename;
                                seedInfo.mediaType = citationObj.media_type;
                                newFilteredSeeds.push(seedInfo);
                            }
                        } catch (error) {
                            console.error(`Failed to fetch seed files `, error);
                        }
                    } else if (citationObj.media_type === "podcast" || citationObj.media_type === "sps_podcast") {
                        try {
                            const podcastCitationsRes = await getPodcastCitationInfo(citationObj.citation);
                            const podcastData = await podcastCitationsRes.data;
                            if (podcastData.status === "success" && podcastData?.podcast_info) {
                                count.sps_podcast += 1;
                                podcastData.podcast_info.mediaType = citationObj.media_type;
                                newFilteredSeeds.push(podcastData.podcast_info);
                            } else if (podcastData.status === "error") {
                                continue;
                            }
                        } catch (error: any) {
                            console.error(`Failed to fetch source info `, error);
                            // setPodcastError(error.message); // Set error message when request fails
                        } finally {
                            // setIsLoading(false);
                        }
                    } else {
                        // for books
                        citationObj.mediaType = citationObj.media_type;
                        newFilteredSeeds.push(citationObj);
                        count.books += 1;
                    }
                }
                setSourceCount(count);
                setSeeds(newFilteredSeeds);
            } catch (err: any) {
                let message = "Something went wrong while searching.";
                if (err.response) {
                    if (err.response.status === 400) {
                        message = err.response.data?.error || "Invalid search request.";
                    } else if (err.response.status === 503) {
                        message = "Search service unavailable. Please try again later.";
                    } else if (err.response.status === 500) {
                        message = "Internal server error. Please try again later.";
                    } else {
                        message = err.response.data?.error || `Unexpected error (${err.response.status}).`;
                    }
                } else if (err.request) {
                    message = "No response from server. Please check your connection.";
                } else {
                    message = err.message || message;
                }

                setError(message);
                console.error("Search error:", err);
            } finally {
                SetSearchLoading(false);
            }
        }
    }, [textAreaValue]);

    const handleKeyDown = useCallback(
        async (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                if (textAreaValue.trim().length !== 0) await searchSeeds();
            }
        },
        [searchSeeds]
    );

    return (
        <div className="">
            <div className=" relative pb-28">
                <CitationExplorer seeds={seeds} searchLoading={searchLoading} error={error} hasSearched={hasSearched} onCitationClick={onCitationClicked}/>
            </div>

            <div className="fixed bottom-0  md:pt-0 dark:border-white/20 w-[calc(100%-6rem)] juice:w-full html_bg_color min-w-80 bg-neutral-100">
                <div className="mt-3 flex items-center justify-center mb-3 ">
                    <textarea
                        ref={textAreaRef}
                        className="rounded-xl shadow-2xl w-8/12 resize-none border p-2 "
                        id="promptInput"
                        placeholder="Search your query"
                        value={textAreaValue}
                        onChange={e => setTextAreaValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        autoFocus
                    />
                    <button onClick={searchSeeds} className="text-green-800 ml-2  p-2 ">
                        <LuSendHorizonal />
                    </button>
                </div>
                {hasSearched && (
                    <div className="mb-2 text-sm text-gray-700 flex gap-4 flex-wrap justify-center">
                        <span>Blogs: {sourcesCount.blogs}</span>
                        <span>Exclusive Content: {sourcesCount.exclusive_content}</span>
                        <span>SPS Podcast: {sourcesCount.sps_podcast}</span>
                        <span>Book: {sourcesCount.books}</span>
                    </div>
                )}
            </div>
            {showCitationSource && (
                <div className="citation-source flex-shrink-0">
                    <ShowCitationExplorerSource style={{ flexGrow: 1 }} open={openRight} onClose={closeDrawerRight} citation={citation} />
                </div>
            )}
        </div>
    );
};

export default Seed;
